'use client';

import { Moon, Sun } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  DEFAULT_THEME,
  THEME_EVENTS,
  THEME_STORAGE_KEY,
  type ThemeMode,
  isThemeMode,
} from '@/lib/theme/constants';
import { cn } from '@/lib/utils';

function getThemeFromDom(): ThemeMode {
  if (typeof document === 'undefined') {
    return DEFAULT_THEME;
  }

  const theme = document.documentElement.dataset.theme ?? null;
  return isThemeMode(theme) ? theme : DEFAULT_THEME;
}

function getStoredTheme(): ThemeMode {
  if (typeof window === 'undefined') {
    return DEFAULT_THEME;
  }

  try {
    const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeMode(storedTheme) ? storedTheme : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

function applyTheme(theme: ThemeMode) {
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;

  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Browsers can block storage in private or restricted contexts.
  }
}

export function ThemeToggle({ className }: { className?: string }) {
  const [theme, setTheme] = useState<ThemeMode>(DEFAULT_THEME);

  useEffect(() => {
    const storedTheme = getStoredTheme();
    applyTheme(storedTheme);
    setTheme(storedTheme);

    const onStorage = (event: StorageEvent) => {
      if (event.key !== THEME_STORAGE_KEY && event.key !== null) {
        return;
      }

      const nextTheme = getStoredTheme();
      applyTheme(nextTheme);
      setTheme(nextTheme);
    };

    const onThemeChanged = () => {
      setTheme(getThemeFromDom());
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener(THEME_EVENTS.changed, onThemeChanged);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(THEME_EVENTS.changed, onThemeChanged);
    };
  }, []);

  const isLight = theme === 'light';
  const nextTheme: ThemeMode = isLight ? 'dark' : 'light';
  const label = isLight ? 'Cambiar a modo noche' : 'Cambiar a modo dia';
  const Icon = isLight ? Moon : Sun;

  const handleClick = () => {
    applyTheme(nextTheme);
    setTheme(nextTheme);
    window.dispatchEvent(new CustomEvent(THEME_EVENTS.changed, { detail: { theme: nextTheme } }));
  };

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={handleClick}
      className={cn(
        'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-button border border-border bg-bg-secondary/80 text-text-secondary transition-colors hover:border-accent hover:text-accent',
        className
      )}
    >
      <Icon size={18} aria-hidden="true" />
    </button>
  );
}
